import React from 'react'
import PropTypes from 'prop-types'
import {Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, IconButton, Tooltip} from '@mui/material'
import {makeStyles} from '@mui/styles'
import {Typography as TypographyStyle, Colors, Spacing} from '../../assets/styles'
import SelectInput from '../SelectInput'
import DashboardToggle from '../DashboardToggle'
import GenericManyInput from '../GenericManyInput'
import RXRIcon from '../RXRIcon'
import RXRButton from '../RXRButton'
import {v4 as uuid} from 'uuid'
import AmenityConstants from './AmenitiesConstants'
import {useSelector} from 'react-redux'
import {CaptionHeader, TrademarkText} from '../../assets/styles/typography'

const useStyles = makeStyles(theme => ({
  title: {
    ...TypographyStyle.H3Header,
    lineHeight: '22px',
    color: Colors.rxrBlackColor,
  },
  toggleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: Spacing.spaceMedium,
  },
  infoIcon: {
    color: Colors.rxrMediumGreyColor,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.info.main,
    },
  },
  linkingContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  addRowText: {
    cursor: 'pointer',
    color: Colors.rxrPrimaryColor,
    marginTop: theme.spacing(1),
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  messageText: {
    fontSize: '16px',
    lineHeight: '19px',
    color: 'rgba(0, 0, 0, 0.6)',
  },
}))

function AmenityLinkRowInput({value, index, onChange, isDisabled, options, onRemove}) {
  const handleChange = (field, newValue) => {
    const updatedValue = {
      ...value, // Preserve all original properties including id, rowId, etc.
      amenityId: value?.amenityId || '',
      relationship: value?.relationship || '',
      rowId: value?.rowId || uuid(),
      [field]: newValue,
    }
    onChange(updatedValue)
  }

  const currentValue = {
    amenityId: value?.amenityId || '',
    relationship: value?.relationship || '',
    rowId: value?.rowId || uuid(),
  }

  // Convert error conditions to boolean
  const amenityError = !!(currentValue.amenityId === '' && currentValue.relationship !== '')
  const relationshipError = !!(currentValue.amenityId !== '' && currentValue.relationship === '')

  return (
    <React.Fragment>
      <div style={{flex: 2}}>
        <SelectInput
          label="Amenity"
          value={currentValue.amenityId || 'AMENITY_PLACEHOLDER'}
          onChange={id => handleChange('amenityId', id === 'AMENITY_PLACEHOLDER' ? '' : id)}
          options={options}
          disabled={isDisabled}
          placeholder="Select an amenity..."
          error={amenityError}
          isRequired={index === 0}
        />
      </div>
      <div style={{alignSelf: 'center', fontWeight: 'bold'}}>is the</div>
      <div style={{flex: 1}}>
        <SelectInput
          label="Relationship"
          value={currentValue.relationship || 'PLACEHOLDER'}
          onChange={rel => handleChange('relationship', rel === 'PLACEHOLDER' ? '' : rel)}
          options={[
            {label: 'Select relationship...', value: 'PLACEHOLDER', isDisabled: true},
            {label: 'Parent', value: 'parent'},
            {label: 'Child', value: 'child'},
          ]}
          disabled={isDisabled || !currentValue.amenityId}
          placeholder="Select relationship..."
          error={relationshipError}
          isRequired={index === 0 && currentValue.amenityId !== ''}
        />
      </div>
    </React.Fragment>
  )
}

AmenityLinkRowInput.propTypes = {
  value: PropTypes.shape({
    amenityId: PropTypes.string,
    relationship: PropTypes.string,
    rowId: PropTypes.string,
  }),
  index: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      isDisabled: PropTypes.bool,
    }),
  ).isRequired,
  onRemove: PropTypes.func,
}

function AmenityLinking({amenityId, linkedAmenities, onLinksChange, isSaving}) {
  const classes = useStyles()

  const amenitiesLookup = useSelector(state => state.Amenities.amenitiesLookup)
  const thisAmenity = amenitiesLookup[amenityId]
  const availableAmenities = Object.values(amenitiesLookup)

  const [localLinks, setLocalLinks] = React.useState(linkedAmenities || [])
  const [showLinked, setShowLinked] = React.useState((linkedAmenities || []).length > 0)
  const [showInfoDialog, setShowInfoDialog] = React.useState(false)

  React.useEffect(() => {
    setLocalLinks(linkedAmenities || [])
    if ((linkedAmenities || []).length > 0) {
      setShowLinked(true)
    }
  }, [linkedAmenities])

  // Normalize links to consistent format for UI
  const normalizedLinks = localLinks.map(link => {
    if (link.hasOwnProperty('amenityId')) {
      // Already in UI format (amenityId property exists, even if empty)
      return {
        ...link,
        rowId: link.rowId || uuid(),
      }
    }
    // Convert database format to UI format (has parentAmenityId/childAmenityId)
    const isParent = link.childAmenityId === amenityId
    return {
      amenityId: isParent ? link.parentAmenityId : link.childAmenityId,
      relationship: isParent ? 'parent' : 'child',
      rowId: link.id || uuid(), // Use original id as rowId, or generate new one
    }
  })

  // Get options for a specific row, filtering out already linked amenities
  const getAmenityOptionsForRow = currentRowValue => {
    const linkedAmenityIds = new Set()

    normalizedLinks.forEach(link => {
      if (link.amenityId && link.amenityId !== currentRowValue?.amenityId) {
        linkedAmenityIds.add(link.amenityId)
      }
    })

    return [
      {label: 'Select an amenity...', value: 'AMENITY_PLACEHOLDER', isDisabled: true},
      ...(availableAmenities || [])
        .filter(a => a.id !== amenityId && !linkedAmenityIds.has(a.id))
        .map(a => ({
          label: a.label || a.name || a.id,
          value: a.id,
          isDisabled: false,
        })),
    ]
  }

  // Simple state tracking - just notify parent of current state
  const handleLinksChange = newLinks => {
    setLocalLinks(newLinks)
    if (typeof onLinksChange === 'function') {
      onLinksChange(newLinks)
    }
  }

  const addEmptyRow = () => {
    const newRow = {amenityId: '', relationship: '', rowId: uuid()}
    const updated = [...localLinks, newRow]
    handleLinksChange(updated)
  }

  const removeLink = obj => {
    // Find the link to remove by matching either rowId or by amenityId+relationship
    const updated = localLinks.filter(link => {
      // If the link has a rowId and it matches obj.rowId, remove it
      if (link.rowId && obj.rowId && link.rowId === obj.rowId) {
        return false
      }

      // If no rowId match, try matching by id (for database links)
      if (link.id && obj.rowId && link.id === obj.rowId) {
        return false
      }

      // If no id match, try matching by amenityId + relationship (for UI links)
      if (link.amenityId === obj.amenityId && link.relationship === obj.relationship) {
        return false
      }

      return true
    })

    handleLinksChange(updated)
  }

  const handleToggleShow = isShowing => {
    if (!isShowing) {
      // if they toggle it off, remove the links
      handleLinksChange([])
    }
    setShowLinked(isShowing)
  }

  return (
    <div>
      <Typography className={classes.title}>{AmenityConstants.TITLE_LINKED_AMENITIES}</Typography>
      <div className={classes.toggleRow} style={{marginTop: Spacing.spaceMedium, marginBottom: Spacing.spaceMedium}}>
        <DashboardToggle
          label={showLinked ? 'Linked amenities enabled' : 'Linked amenities disabled'}
          value={showLinked}
          onChange={handleToggleShow}
        />
        <Tooltip title="More info" enterDelay={1000} leaveDelay={100}>
          <IconButton onClick={() => setShowInfoDialog(true)} aria-label="Amenity linking info">
            <RXRIcon icon={RXRIcon.INFO} className={classes.infoIcon} />
          </IconButton>
        </Tooltip>
      </div>

      <Dialog open={showInfoDialog} onClose={() => setShowInfoDialog(false)}>
        <DialogTitle className={classes.dialogTitle}>
          <RXRIcon icon={RXRIcon.INFO} color={Colors.rxrBlackColor} />
          <Typography className={classes.title}>{AmenityConstants.TITLE_LINKED_AMENITIES}</Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText className={classes.messageText}>
            Amenity linking allows you to restrict access to certain amenities based on the availability of other amenities. For example, if
            you have an amenity like a "rooftop" with a "pool" and a "lounge", residents might be able to reserve the pool, or the lounge, but
            a resident that reserves the whole rooftop will block other residents from reserving the pool or lounge at that time. In this
            example, the "rooftop" is the parent amenity, and the "pool" and "lounge" are child amenities.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <RXRButton type={RXRButton.TYPE_TEXT_SECONDARY} onClick={() => setShowInfoDialog(false)}>
            Close
          </RXRButton>
        </DialogActions>
      </Dialog>

      {showLinked && (
        <React.Fragment>
          <GenericManyInput
            value={normalizedLinks}
            emptyModel={{amenityId: '', relationship: ''}}
            singleInputComponent={({value, ...props}) => (
              <AmenityLinkRowInput {...props} value={value} options={getAmenityOptionsForRow(value)} />
            )}
            onChange={handleLinksChange}
            onAdd={() => {}} // No immediate saving
            onRemove={removeLink}
            showPlusIcon={false}
            validator={obj => !!(obj?.amenityId && obj?.relationship)}
          />
        </React.Fragment>
      )}
    </div>
  )
}

AmenityLinking.propTypes = {
  amenityId: PropTypes.string.isRequired,
  linkedAmenities: PropTypes.array, // Array of AmenityLink objects from database
  onLinksChange: PropTypes.func, // Called with current UI state (normalized links)
  isSaving: PropTypes.bool,
}

export default AmenityLinking
